// 게임이 어느 환경(BE)을 바라봐야 하는지 런타임에 결정한다.
//
// 우선순위:
//   1) `?env=dev|local` URL 쿼리 (테스트용 명시 override; `prod` 는 의도적으로 무시)
//   2) document.referrer hostname (iframe / 새 탭 양쪽 모두 작동)
//       me.bloomingo.team  → prod
//       dev.bloomingo.team → dev
//       localhost          → local
//   3) 본인 hostname 이 localhost → local (로컬 개발용)
//   4) 그 외 → 'unknown' (비정상 접근 → 호출자가 에러 화면 표시)
//
// 빌드 타임에 `VITE_API_BASE_URL` 가 박혀 있으면 모든 분기 무시.

export type Env = 'local' | 'dev' | 'prod';
export type ResolvedEnv = Env | 'unknown';

const PROD_PARENT_HOST = 'me.bloomingo.team';
const DEV_PARENT_HOST = 'dev.bloomingo.team';

export const API_BASE_BY_ENV: Record<Env, string> = {
  local: '', // 빈 문자열 = LocalRankingService 폴백
  dev: 'https://dev-bloomingo-be-936355912716.asia-northeast3.run.app/api',
  prod: 'https://prod-bloomingo-be-936355912716.asia-northeast3.run.app/api',
};

// dev 환경에서 직접 URL 진입 테스트용 — POST /auth/tokens 로 자동 로그인.
// dev 외 환경에선 절대 사용 안 함.
export const DEV_TEST_CREDENTIALS = {
  email: 'test',
  password: '1234',
} as const;

export function resolveEnv(): ResolvedEnv {
  if (typeof window === 'undefined') return 'prod';

  const params = new URLSearchParams(window.location.search);
  const q = params.get('env');
  // `prod` 는 직접 URL 위장 차단 — 명시 override 인정 안 함
  if (q === 'dev' || q === 'local') return q;

  if (document.referrer) {
    try {
      const refHost = new URL(document.referrer).hostname;
      if (refHost === 'localhost' || refHost === '127.0.0.1') return 'local';
      if (refHost === DEV_PARENT_HOST) return 'dev';
      if (refHost === PROD_PARENT_HOST) return 'prod';
    } catch {
      // ignore parse error
    }
  }

  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'local';

  return 'unknown';
}

// null 반환 = 비정상 접근 (env 결정 불가). 호출자는 에러 UI 노출.
export function resolveApiBaseUrl(): string | null {
  const explicit = import.meta.env.VITE_API_BASE_URL;
  if (explicit && explicit.length > 0) return explicit;

  const env = resolveEnv();
  if (env === 'unknown') return null;
  return API_BASE_BY_ENV[env];
}

// `?env=dev` 으로 명시적 진입한 경우만 true.
// referrer 기반으로 dev 가 된 경우는 false (FE 가 postMessage 로 토큰 전달할 거란 가정).
export function isExplicitDevQuery(): boolean {
  if (typeof window === 'undefined') return false;
  return new URLSearchParams(window.location.search).get('env') === 'dev';
}
